import { ApiException, PasswordProtectedTransfer, Transfer } from "../entities";

const isApiException = (response: any): response is ApiException => {
  const doesErrorExist: boolean = response.hasOwnProperty("error");
  if (!doesErrorExist) return false;

  const error: Object = response.error;

  const doesCodeFieldExist: boolean = error.hasOwnProperty("code");
  const doesMessageFieldExist: boolean = error.hasOwnProperty("message");
  const doesDetailsFieldExist: boolean = error.hasOwnProperty("details");
  const doesValidationFieldExist: boolean = error.hasOwnProperty("validation");

  return (
    doesCodeFieldExist &&
    doesMessageFieldExist &&
    doesDetailsFieldExist &&
    doesValidationFieldExist
  );
};

const isPasswordProtectedTransfer = (
  transfer: Transfer | PasswordProtectedTransfer
): transfer is PasswordProtectedTransfer => {
  return "passwordAttempts" in transfer;
};

const isTransfer = (
  transfer: Transfer | PasswordProtectedTransfer
): transfer is Transfer => {
  return "senderAddress" in transfer;
};

/**
 * Extracts a user-facing error message from an ApiException.
 * If both message and details are present, both are shown.
 * Newline separates message/details for clarity.
 *
 * @param apiException The ApiException object
 * @param t Optional translation function for fallback
 * @return {string} Combined, readable error message for UI
 */
function getErrorMessage(
  apiException: ApiException,
  t: (key: string) => string
): string {
  const { message, details } = apiException.error;
  const hasMsg = !!message;
  const hasDetails = Array.isArray(details) && details.length > 0;

  if (hasMsg && hasDetails) {
    return `${message}\n${details.join(" ")}`;
  } else if (hasDetails) {
    return details.join(" ");
  } else if (hasMsg) {
    return message;
  }
  return t(
    "Something went wrong. If refreshing the page does not fix the problem please contact our support team."
  );
}

function assertNever(x: never): never {
  throw new Error("Unexpected object: " + x);
}

export {
  assertNever,
  isApiException,
  isPasswordProtectedTransfer,
  isTransfer,
  getErrorMessage,
};
